import { useState } from "react";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export const field = "w-full rounded-2xl bg-white/[0.03] border border-white/10 px-4 py-3.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#d5d9df]/70";

const ForgotForm = ({ email, setEmail, sent, busy, error, onSubmit, onBack }) => (
  <form onSubmit={onSubmit} data-testid="forgot-form">
    <button type="button" data-testid="forgot-back" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors mb-5">
      <ArrowLeft className="h-4 w-4" /> Back to sign in
    </button>
    <h2 className="font-display text-lg font-semibold mb-1">Reset your password</h2>
    {sent ? (
      <div data-testid="forgot-sent" className="flex gap-3 rounded-2xl border border-[#d5d9df]/30 bg-[#3b4049]/10 p-4 mt-4">
        <MailCheck className="h-5 w-5 text-[#d5d9df] shrink-0 mt-0.5" />
        <p className="text-sm text-slate-200">If an account exists for {email}, a reset link is on its way. It expires in 1 hour.</p>
      </div>
    ) : (
      <>
        <p className="text-slate-400 text-sm mb-5">Enter your account email and we'll send you a link to choose a new password.</p>
        <input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
        {error && <p data-testid="forgot-error" className="text-red-400 text-sm mt-4">{error}</p>}
        <button data-testid="forgot-submit" disabled={busy} className="btn-primary w-full justify-center py-4 mt-6">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
        </button>
      </>
    )}
  </form>
);

export const AuthForm = ({ initialMode = "login", onDone }) => {
  const { setUser } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const isLogin = mode === "login";
  const isForgot = mode === "forgot";

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setSent(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isForgot) {
        await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
        setSent(true);
        return;
      }
      const { data } = isLogin
        ? await api.post("/auth/login", { email, password })
        : await api.post("/auth/register", { name, email, password });
      setUser(data);
      toast.success(isLogin ? `Welcome back${data?.name ? `, ${data.name.split(" ")[0]}` : ""}` : "Account created");
      onDone?.(data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (isForgot) {
    return (
      <ForgotForm email={email} setEmail={setEmail} sent={sent} busy={busy} error={error} onSubmit={submit} onBack={() => switchMode("login")} />
    );
  }

  return (
    <form onSubmit={submit} data-testid="auth-form">
      <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-white/5 mb-6">
        {["login", "register"].map((m) => (
          <button
            key={m}
            type="button"
            data-testid={`auth-tab-${m}`}
            onClick={() => switchMode(m)}
            className={`py-2.5 rounded-full text-sm font-medium transition-colors ${mode === m ? "bg-[#3b4049] text-white" : "text-slate-300 hover:text-white"}`}
          >
            {m === "login" ? "Sign in" : "Create account"}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {!isLogin && <input data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={field} />}
        <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
        <input
          data-testid="auth-password-input"
          type="password"
          required
          minLength={isLogin ? undefined : 8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={isLogin ? "Password" : "Password (8+ characters)"}
          className={field}
        />
      </div>
      {isLogin && (
        <button type="button" data-testid="auth-forgot-link" onClick={() => switchMode("forgot")} className="text-sm text-[#d5d9df] hover:text-white transition-colors mt-3">
          Forgot password?
        </button>
      )}
      {error && <p data-testid="auth-error" className="text-red-400 text-sm mt-4">{error}</p>}
      <button data-testid="auth-submit" disabled={busy} className="btn-primary w-full justify-center py-4 mt-6">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : isLogin ? "Sign in" : "Create account"}
      </button>
    </form>
  );
};
